import { DocumentNode } from 'graphql';
import { ShoppingBag, Tag, Trash } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  GetDiscountQuery as GetDiscount,
  GetDiscountsQuery as GetDiscounts,
} from '__generated__/graphql';

import { Option } from '../../../../common/Common/OptionsDropdown';
import { encodeUrl } from '../../../../common/helpers';
import { useDiscountStatus } from './DiscountStatusIndicator';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import ActivateDiscountMutation from './ActivateDiscountMutation';
import CardPreview from '../CardPreview';
import DeactivateDiscountMutation from './DeactivateDiscountMutation';
import DeleteDiscountMutation from './DeleteDiscountMutation';
import DiscountValue from '../../../../common/Common/DiscountValue';
import EditDiscountForm from './EditDiscountForm';
import GetDiscountsQuery from './GetDiscountsQuery';
import UI from '../../../../common/UI';
import useConfirmModal from '../../../../common/useConfirmModal';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../../common/useProject';

type Event = GetDiscounts['project']['event'];
type Discount = Event['discounts'][0];

interface DiscountListProps {
  event: Event;
  onResetFilters: () => void;
  activeDiscountId: string;
  discountData?: GetDiscount;
  refetchQueries?: DocumentNode[];
}

const DiscountList = ({
  event, onResetFilters, activeDiscountId, discountData, refetchQueries = [GetDiscountsQuery],
}: DiscountListProps) => {
  const project = useProject();
  const { t } = useTranslation();

  const hasFilteredDiscounts = event.discounts.length > 0;

  return (
    <UI.Div>
      {hasFilteredDiscounts ? (
        <UI.GridContainer sc={{ gutter: 1 / 3 }}>
          {event.discounts.map((discount, index) => (
            <UI.CollapsibleNavCard
              id={`DiscountCard_${discount.id}`}
              key={index}
              to={`/${project.id}/events/${event.id}/discounts/${activeDiscountId !== discount.id ? discount.id : ''}`}
              open={discount.id === activeDiscountId}
              muted={!!activeDiscountId && discount.id !== activeDiscountId}
              preview={(
                <DiscountPreview
                  event={event}
                  discount={discount}
                  refetchQueries={refetchQueries}
                />
              )}
              body={discountData
                ? <EditDiscountForm discount={discountData.discount} event={discountData.event} />
                : <UI.PageLoader />}
            />
          ))}
        </UI.GridContainer>
      ) : (
        <UI.Card
          sc={{ padding: 8, textAlign: 'center' }}
        >
          <UI.Div sc={{ color: 'secondary', mb: 2 }}>
            <Tag strokeWidth={0.5} size={60} />
          </UI.Div>
          <UI.Span>{t('no_discounts_match_filters')}</UI.Span>
          {' '}
          <UI.A onClick={onResetFilters}>{t('reset_filters')}</UI.A>
        </UI.Card>
      )}
    </UI.Div>
  );
};

interface DiscountPreviewProps {
  event: Event;
  discount: Discount;
  refetchQueries?: DocumentNode[];
}

const DiscountPreview = ({ event, discount, refetchQueries }: DiscountPreviewProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const notifier = useNotifier();
  const history = useHistory();

  const project = useProject();

  const [activateDiscount] = useMutation(ActivateDiscountMutation, {
    variables: {
      id: discount.id,
    },
    optimisticResponse: {
      activateDiscount: {
        id: discount.id,
        active: true,
        __typename: 'Discount',
      },
    },
    onCompleted: () => notifier.success(t('notifications.discount_activated')),
  });

  const [deactivateDiscount] = useMutation(DeactivateDiscountMutation, {
    variables: {
      id: discount.id,
    },
    optimisticResponse: {
      deactivateDiscount: {
        id: discount.id,
        active: false,
        __typename: 'Discount',
      },
    },
    onCompleted: () => notifier.success(t('notifications.discount_deactivated')),
  });

  const toggleStatus = () => {
    if (discount.active) {
      deactivateDiscount();
    } else {
      activateDiscount();
    }
  };

  const [deleteDiscount, { loading: deleting }] = useMutation(
    DeleteDiscountMutation, {
      variables: {
        id: discount.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.discount_deleted'));

        history.push(`/${project.id}/events/${event.id}/discounts`);
      },
      onError: () => notifier.error(t('something_went_wrong')),
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: discount.title }),
    onConfirm: () => deleteDiscount(),
  }, [discount]);

  const levels = discount.levels.map(({ value }) => value);
  const maxDiscount = Math.max(...levels);
  const minDiscount = Math.min(...levels);

  const { message } = useDiscountStatus(discount);

  return (
    <CardPreview
      title={discount.title}
      firstColumn={levels.length > 0 && (
      <>
        {minDiscount === maxDiscount && (
          <DiscountValue discount={{ type: discount.type, value: minDiscount }} />
        )}
        {minDiscount !== maxDiscount && (
          <>
            <DiscountValue discount={{ type: discount.type, value: minDiscount }} />
            {' - '}
            <DiscountValue discount={{ type: discount.type, value: maxDiscount }} />
          </>
        )}
      </>
      )}
      secondColumn={(
        <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
          <UI.Link
            to={`/${project.id}/participants/orders?${encodeUrl({
              event: event.id,
              discounts: [discount.id],
            })}`}
            sc={{ secondary: true }}
          >
            <UI.Icon>
              <ShoppingBag />
            </UI.Icon>
            {' '}
            {formatInteger(discount.uses)}
          </UI.Link>
          <UI.Tooltip sc={{ alignH: 'center' }}>
            {t('times_applied')}
          </UI.Tooltip>
        </UI.TooltipContainer>
      )}
      status={message}
      active={discount.active}
      onToggle={toggleStatus}
      options={() => (
        <Option
          icon={<Trash />}
          label={t('delete')}
          onClick={() => confirmDelete()}
          disabled={!discount.deletable || deleting}
        />
      )}
    />
  );
};

export default DiscountList;
